/**
 * ErvaPOS - Stock/Batches Management Page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Search,
  Package,
  Loader2,
  Save,
  AlertTriangle,
  Tag,
  Printer,
  QrCode,
  Calendar,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ClipboardList,
  PencilLine,
  History,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import api, { batchesAPI, productsAPI, suppliersAPI, labelsAPI } from '../services/api';
import { toast } from 'sonner';

const initialBatchForm = {
  product_id: '',
  lot_number: '',
  expiry_date: '',
  quantity: 1,
  cost_price: 0,
  sell_price: 0,
  supplier_id: '',
  notes: '',
};


const initialEditProductForm = {
  id: '',
  name: '',
  reference: '',
  barcode: '',
  cost_price: 0,
  sell_price: 0,
  min_stock: 0,
};

export const StockPage = () => {
  const location = useLocation();

  const getExpiryStyle = (status) => {
    switch (status) {
      case "expired":
        return "text-red-500 font-semibold";
      case "critical":
        return "text-orange-400 font-semibold";
      case "warning":
        return "text-yellow-400";
      case "attention":
        return "text-blue-400";
      case "ok":
        return "text-green-400";
      default:
        return "text-slate-300";
    }
  };

  const getExpiryLabel = (status) => {
    switch (status) {
      case "expired": return "Expirado";
      case "critical": return "Crítico";
      case "warning": return "A vigiar";
      case "attention": return "Atenção";
      case "ok": return "OK";
      default: return "";
    }
  };


  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const compareValues = (a, b, direction = "asc") => {
    const dir = direction === "asc" ? 1 : -1;
    const av = a ?? "";
    const bv = b ?? "";

    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }

    return String(av).localeCompare(String(bv), 'pt', {
      numeric: true,
      sensitivity: 'base'
    }) * dir;
  };


  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 opacity-60" />;
    return sortDirection === "asc"
      ? <ArrowUp className="w-3.5 h-3.5" />
      : <ArrowDown className="w-3.5 h-3.5" />;
  };


  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [expiringBatches, setExpiringBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [productModalSearch, setProductModalSearch] = useState("");
  const [expiryFilter, setExpiryFilter] = useState(90);
  const [expiryStatusFilter, setExpiryStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [onlyBelowMinStock, setOnlyBelowMinStock] = useState(false);
  const [onlyNoStock, setOnlyNoStock] = useState(false);
  const [onlyExpired, setOnlyExpired] = useState(false);
  const [sortField, setSortField] = useState("product");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    return ["batches", "expiring", "labels"].includes(tab) ? tab : "batches";
  });
  const [expandedProductId, setExpandedProductId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qsStatus = (params.get("expiry_status") || "all").trim();
    if (["all", "expired", "critical", "warning", "attention", "ok"].includes(qsStatus)) {
      setExpiryStatusFilter(qsStatus);
    }
    const qsTab = params.get("tab");
    if (["batches", "expiring", "labels"].includes(qsTab)) {
      setActiveTab(qsTab);
    } else if (qsStatus !== "all") {
      setActiveTab("expiring");
    }
  }, [location.search]);
  const [showModal, setShowModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [formData, setFormData] = useState(initialBatchForm);
  const [editProductForm, setEditProductForm] = useState(initialEditProductForm);
  const [saving, setSaving] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [labelQuantity, setLabelQuantity] = useState(1);
  const [labelData, setLabelData] = useState(null);
  const [pendingLabels, setPendingLabels] = useState([]);
  const [showPendingLabelsModal, setShowPendingLabelsModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showCorrectionHistoryModal, setShowCorrectionHistoryModal] = useState(false);
  const [correctionBatch, setCorrectionBatch] = useState(null);
  const [correctionHistory, setCorrectionHistory] = useState([]);
  const [correctionForm, setCorrectionForm] = useState({ quantity: 0, lot_number: '', expiry_date: '', reason: '' });
  const [correcting, setCorrecting] = useState(false);
  const [inventoryAsOf, setInventoryAsOf] = useState(() => new Date().toISOString().split('T')[0]);

  const downloadInventory = async (kind) => {
    try {
      const response = await api.get(`/inventory/export/${kind}`, { params: { as_of: inventoryAsOf }, responseType: 'blob' });
      const extension = kind === 'pdf' ? 'pdf' : kind.endsWith('xml') ? 'xml' : 'csv';
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventario_${inventoryAsOf}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Inventário preparado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível preparar o inventário');
    }
  };

  const openCorrectionModal = (batch) => {
    setCorrectionBatch(batch);
    setCorrectionForm({ quantity: Number(batch.quantity || 0), lot_number: batch.lot_number || '', expiry_date: batch.expiry_date || '', reason: '' });
    setShowCorrectionModal(true);
  };

  const submitCorrection = async () => {
    if (!correctionBatch) return;
    if (correctionForm.reason.trim().length < 5) return toast.error('Indique uma justificação com pelo menos 5 caracteres');
    try {
      setCorrecting(true);
      await batchesAPI.correct(correctionBatch.id, correctionForm);
      toast.success('Lote corrigido e alteração registada');
      setShowCorrectionModal(false);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao corrigir lote');
    } finally { setCorrecting(false); }
  };

  const openCorrectionHistory = async (batch) => {
    try {
      setCorrectionBatch(batch);
      const response = await batchesAPI.getCorrections(batch.id);
      setCorrectionHistory(response.data || []);
      setShowCorrectionHistoryModal(true);
    } catch (error) { toast.error(error?.response?.data?.detail || 'Erro ao carregar histórico'); }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [batchesRes, expiringRes, productsRes, suppliersRes, pendingLabelsRes] = await Promise.all([
        batchesAPI.getAll(),
        batchesAPI.getExpiring(expiryFilter),
        productsAPI.getAll(),
        suppliersAPI.getAll(),
        batchesAPI.getPendingLabels().catch(() => ({ data: [] })),
      ]);
      setBatches(batchesRes.data || []);
      setExpiringBatches(expiringRes.data || []);
      setProducts(productsRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setPendingLabels(pendingLabelsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [expiryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getDaysUntilExpiry = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadge = (days) => {
    if (days <= 30) return <Badge className="expiry-critical">Crítico - {days} dias</Badge>;
    if (days <= 60) return <Badge className="expiry-warning">Atenção - {days} dias</Badge>;
    return <Badge className="expiry-ok">{days} dias</Badge>;
  };

  const filteredBatches = batches.filter(b => {
    if (expiryStatusFilter !== "all") {
      if (b.expiry_status !== expiryStatusFilter) return false;
    }

    if (supplierFilter !== "all") {
      if ((b.supplier_id || "") !== supplierFilter) return false;
    }

    if (!searchQuery) return true;

    const product = products.find(p => p.id === b.product_id);
    return (
      (product?.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.lot_number || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const chronologicalExpiringBatches = [...expiringBatches].sort((a, b) =>
    String(a.expiry_date || '9999-12-31').localeCompare(String(b.expiry_date || '9999-12-31'))
  );

  const groupedFilteredProducts = (() => {
    const grouped = {};

    filteredBatches.forEach((b) => {
      const pid = b.product_id;
      const product = products.find((p) => p.id === pid);

      if (!grouped[pid]) {
        grouped[pid] = {
          product,
          batches: [],
          totalStock: 0,
          hasExpiredLots: false,
        };
      }

      grouped[pid].batches.push(b);
      grouped[pid].totalStock += Number(b.quantity || 0);

      if (b.expiry_status === "expired") {
        grouped[pid].hasExpiredLots = true;
      }
    });

    return Object.fromEntries(
      Object.entries(grouped).filter(([, group]) => {
        const minStock = Number(group.product?.min_stock || 0);
        const totalStock = Number(group.totalStock || 0);

        if (onlyBelowMinStock && !(totalStock < minStock)) return false;
        if (onlyNoStock && !(totalStock <= 0)) return false;
        if (onlyExpired && !group.hasExpiredLots) return false;

        return true;
      })
    );
  })();


  const stockSummary = (() => {
    const grouped = {};

    batches.forEach((b) => {
      const pid = b.product_id;
      const product = products.find((p) => p.id === pid);

      if (!grouped[pid]) {
        grouped[pid] = {
          product,
          totalStock: 0,
          hasExpiredLots: false,
          lotsCount: 0,
        };
      }

      grouped[pid].totalStock += Number(b.quantity || 0);
      grouped[pid].lotsCount += 1;

      if (b.expiry_status === "expired") {
        grouped[pid].hasExpiredLots = true;
      }
    });

    const groups = Object.values(grouped);

    return {
      totalProductsWithLots: groups.length,
      belowMin: groups.filter((g) => Number(g.totalStock || 0) < Number(g.product?.min_stock || 0)).length,
      noStock: groups.filter((g) => Number(g.totalStock || 0) <= 0).length,
      expired: groups.filter((g) => g.hasExpiredLots).length,
    };
  })();


  const getSortedGroupedEntries = () => {
    const grouped = groupedFilteredProducts;

    let groupedEntries = Object.entries(grouped);

    if (sortField === 'product') {
      groupedEntries = groupedEntries.sort(([, a], [, b]) =>
        compareValues(a.product?.name || '', b.product?.name || '', sortDirection)
      );
    } else {
      groupedEntries = groupedEntries.sort(([, a], [, b]) =>
        compareValues(a.product?.name || '', b.product?.name || '', 'asc')
      );
    }

    return groupedEntries.map(([pid, group]) => {
      const sortedGroupBatches = [...group.batches].sort((a, b) => {
        switch (sortField) {
          case 'lot_number':
            return compareValues(a.lot_number || '', b.lot_number || '', sortDirection);
          case 'expiry_date':
            return compareValues(a.expiry_date || '', b.expiry_date || '', sortDirection);
          case 'quantity':
            return compareValues(Number(a.quantity || 0), Number(b.quantity || 0), sortDirection);
          case 'cost_price':
            return compareValues(Number(a.cost_price || 0), Number(b.cost_price || 0), sortDirection);
          default:
            return compareValues(a.expiry_date || '', b.expiry_date || '', 'asc');
        }
      });

      return [pid, { ...group, batches: sortedGroupBatches }];
    });
  };

  const getExportRows = () => {
    const groupedEntries = getSortedGroupedEntries();
    const rows = [];

    groupedEntries.forEach(([, group]) => {
      group.batches.forEach((batch) => {
        rows.push({
          product_name: group.product?.name || 'Desconhecido',
          reference: group.product?.reference || '',
          barcode: group.product?.barcode || '',
          supplier_name: suppliers.find((s) => s.id === (batch.supplier_id || ''))?.name || '',
          lot_number: batch.lot_number || '',
          expiry_date: batch.expiry_date || '',
          expiry_status: getExpiryLabel(batch.expiry_status || '') || '',
          quantity: Number(batch.quantity || 0),
          cost_price: Number(batch.cost_price || 0),
          sell_price: Number(group.product?.sell_price || 0),
          min_stock: Number(group.product?.min_stock || 0),
          total_stock_product: Number(group.totalStock || 0),
        });
      });
    });

    return rows;
  };

  const exportStockCSV = () => {
    const rows = getExportRows();

    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }

    const headers = [
      'Produto',
      'Referência',
      'Código de barras',
      'Fornecedor',
      'Lote',
      'Validade',
      'Estado validade',
      'Quantidade lote',
      'Custo',
      'PVP',
      'Stock mínimo',
      'Stock total produto'
    ];

    const escapeCSV = (value) => {
      const s = String(value ?? '');
      if (s.includes(';') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const lines = [
      headers.join(';'),
      ...rows.map((r) => ([
        r.product_name,
        r.reference,
        r.barcode,
        r.supplier_name,
        r.lot_number,
        r.expiry_date,
        r.expiry_status,
        r.quantity,
        r.cost_price.toFixed(2),
        r.sell_price.toFixed(2),
        r.min_stock,
        r.total_stock_product
      ].map(escapeCSV).join(';')))
    ];

    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `stock_filtrado_${today}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exportado');
  };

  const exportStockPDF = () => {
    const rows = getExportRows();

    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }

    const today = new Date();
    const generatedAt = today.toLocaleString('pt-PT');

    const activeFilters = [
      searchQuery ? `Pesquisa: ${searchQuery}` : null,
      supplierFilter !== 'all'
        ? `Fornecedor: ${suppliers.find((s) => s.id === supplierFilter)?.name || supplierFilter}`
        : null,
      onlyBelowMinStock ? 'Abaixo do mínimo' : null,
      onlyNoStock ? 'Sem stock' : null,
      onlyExpired ? 'Com expirados' : null,
      expiryStatusFilter !== 'all' ? `Estado validade: ${getExpiryLabel(expiryStatusFilter) || expiryStatusFilter}` : null,
      `Ordenação: ${sortField} (${sortDirection === 'asc' ? 'asc' : 'desc'})`
    ].filter(Boolean).join(' | ');

    const tableRows = rows.map((r) => `
      <tr>
        <td>${r.product_name}</td>
        <td>${r.reference || '—'}</td>
        <td>${r.lot_number || '—'}</td>
        <td>${r.expiry_date || '—'}</td>
        <td>${r.expiry_status || '—'}</td>
        <td style="text-align:right;">${r.quantity}</td>
        <td style="text-align:right;">€ ${r.cost_price.toFixed(2)}</td>
        <td style="text-align:right;">€ ${r.sell_price.toFixed(2)}</td>
        <td style="text-align:right;">${r.total_stock_product}</td>
      </tr>
    `).join('');

    const html = `
      <html>
        <head>
          <title>Stock Filtrado</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 24px;
              color: #111827;
            }
            h1 {
              margin: 0 0 8px 0;
              font-size: 22px;
            }
            .meta {
              font-size: 12px;
              color: #4b5563;
              margin-bottom: 16px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 6px 8px;
              vertical-align: top;
            }
            th {
              background: #f3f4f6;
              text-align: left;
            }
            .summary {
              margin-bottom: 14px;
              font-size: 12px;
            }
            @media print {
              body {
                margin: 12mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>Listagem Filtrada de Stock</h1>
          <div class="meta">Gerado em: ${generatedAt}</div>
          <div class="summary"><strong>Filtros:</strong> ${activeFilters || 'Nenhum'}</div>
          <table>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Referência</th>
                <th>Lote</th>
                <th>Validade</th>
                <th>Estado</th>
                <th>Qtd lote</th>
                <th>Custo</th>
                <th>PVP</th>
                <th>Stock total</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            };
          <\/script>
        </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Falha ao abrir janela de impressão');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };


  const getExpiringExportRows = () => {
    return (expiringBatches || []).map((batch) => {
      const product = products.find((p) => p.id === batch.product_id);
      const supplier = suppliers.find((s) => s.id === (batch.supplier_id || ''));
      const daysLeft = getDaysUntilExpiry(batch.expiry_date);

      return {
        product_name: batch.product_name || product?.name || 'Desconhecido',
        reference: product?.reference || '',
        barcode: product?.barcode || '',
        supplier_name: supplier?.name || '',
        lot_number: batch.lot_number || '',
        expiry_date: batch.expiry_date || '',
        quantity: Number(batch.quantity || 0),
        cost_price: Number(batch.cost_price || 0),
        sell_price: Number(product?.sell_price || 0),
        days_left: Number(daysLeft),
      };
    });
  };

  const exportExpiringCSV = () => {
    const rows = getExpiringExportRows();

    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }

    const headers = [
      'Produto',
      'Referência',
      'Código de barras',
      'Fornecedor',
      'Lote',
      'Validade',
      'Dias até expirar',
      'Quantidade lote',
      'Custo',
      'PVP'
    ];

    const escapeCSV = (value) => {
      const s = String(value ?? '');
      if (s.includes(';') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const lines = [
      headers.join(';'),
      ...rows.map((r) => ([
        r.product_name,
        r.reference,
        r.barcode,
        r.supplier_name,
        r.lot_number,
        r.expiry_date,
        r.days_left,
        r.quantity,
        r.cost_price.toFixed(2),
        r.sell_price.toFixed(2)
      ].map(escapeCSV).join(';')))
    ];

    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `stock_a_expirar_${expiryFilter}dias_${today}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV de expirados exportado');
  };

  const exportExpiringPDF = () => {
    const rows = getExpiringExportRows();

    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }

    const generatedAt = new Date().toLocaleString('pt-PT');

    const tableRows = rows.map((r) => `
      <tr>
        <td>${r.product_name}</td>
        <td>${r.reference || '—'}</td>
        <td>${r.lot_number || '—'}</td>
        <td>${r.expiry_date || '—'}</td>
        <td style="text-align:right;">${r.days_left}</td>
        <td style="text-align:right;">${r.quantity}</td>
        <td style="text-align:right;">€ ${r.cost_price.toFixed(2)}</td>
        <td style="text-align:right;">€ ${r.sell_price.toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <html>
        <head>
          <title>Produtos a Expirar</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 24px;
              color: #111827;
            }
            h1 {
              margin: 0 0 8px 0;
              font-size: 22px;
            }
            .meta {
              font-size: 12px;
              color: #4b5563;
              margin-bottom: 16px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 6px 8px;
              vertical-align: top;
            }
            th {
              background: #f3f4f6;
              text-align: left;
            }
            @media print {
              body {
                margin: 12mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>Produtos a Expirar</h1>
          <div class="meta">Gerado em: ${generatedAt}</div>
          <div class="meta">Filtro atual: próximos ${expiryFilter} dias</div>
          <table>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Referência</th>
                <th>Lote</th>
                <th>Validade</th>
                <th>Dias</th>
                <th>Qtd lote</th>
                <th>Custo</th>
                <th>PVP</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            };
          <\/script>
        </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Falha ao abrir janela de impressão');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };


  const filteredModalProducts = products
    .filter((p) => p?.is_active !== false)
    .filter((p) => {
      const q = (productModalSearch || "").trim().toLowerCase();
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.reference || "").toLowerCase().includes(q) ||
        (p.barcode || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 12);

  const selectedProduct = products.find((p) => p.id === formData.product_id);


  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      cost_price: product?.cost_price || 0,
      sell_price: product?.sell_price || 0,
      supplier_id: product?.supplier_id || '',
    }));
    setProductModalSearch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await batchesAPI.create(formData);
      toast.success('Lote registado com sucesso');
      setShowModal(false);
      setFormData(initialBatchForm);
      loadData();
    } catch (error) {
      toast.error('Erro ao registar lote');
    } finally {
      setSaving(false);
    }
  };

  const openEditProductModal = (product) => {
    if (!product) return;
    setEditProductForm({
      id: product.id || '',
      name: product.name || '',
      reference: product.reference || '',
      barcode: product.barcode || '',
      cost_price: Number(product.cost_price || 0),
      sell_price: Number(product.sell_price || 0),
      min_stock: Number(product.min_stock || 0),
    });
    setShowEditProductModal(true);
  };

  const handleSaveProductQuickEdit = async () => {
    if (!editProductForm.id) return;
    setSaving(true);

    try {
      const currentProduct = products.find((p) => p.id === editProductForm.id);
      await productsAPI.update(editProductForm.id, {
        ...(currentProduct || {}),
        name: editProductForm.name,
        reference: editProductForm.reference,
        barcode: editProductForm.barcode,
        cost_price: Number(editProductForm.cost_price || 0),
        sell_price: Number(editProductForm.sell_price || 0),
        min_stock: Number(editProductForm.min_stock || 0),
      });

      setFormData(prev => (
        prev.product_id === editProductForm.id
          ? {
              ...prev,
              cost_price: Number(editProductForm.cost_price || 0),
              sell_price: Number(editProductForm.sell_price || 0),
            }
          : prev
      ));

      toast.success('Produto atualizado');
      setShowEditProductModal(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar produto');
    } finally {
      setSaving(false);
    }
  };

  const openLabelModal = (batch) => {
    const product = products.find(p => p.id === batch.product_id);
    setSelectedBatch({ ...batch, product_name: batch.product_name || product?.name });
    setLabelQuantity(1);
    setLabelData(null);
    setShowLabelModal(true);
  };

  const openPendingLabelFromRow = (batch) => {
    setShowPendingLabelsModal(false);
    openLabelModal(batch);
  };

  const markPendingLabelPrinted = async (batchId) => {
    try {
      await batchesAPI.markLabelsPrinted(batchId);
      toast.success('Etiqueta marcada como impressa');
      await loadData();
    } catch (error) {
      toast.error('Erro ao marcar etiqueta como impressa');
    }
  };

  const generateLabels = async () => {
    if (!selectedBatch) return;

    try {
      const response = await labelsAPI.generate(
        selectedBatch.product_id,
        selectedBatch.id,
        labelQuantity
      );
      setLabelData(response.data);
      toast.success('Etiquetas geradas');
    } catch (error) {
      toast.error('Erro ao gerar etiquetas');
    }
  };

  const downloadLabelsPDF = async () => {
    if (!selectedBatch) return;
    try {
      const response = await labelsAPI.generatePDF(
        selectedBatch.product_id,
        selectedBatch.id,
        labelQuantity,
        "FAMASAÚDE ESPAÇO SAÚDE"
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.focus();
          printWindow.print();
        });
      }
      try {
        await batchesAPI.markLabelsPrinted(selectedBatch.id);
      } catch (_) {}
      await loadData();
      toast.success("Etiquetas prontas para imprimir!");
    } catch (error) {
      toast.error("Erro ao gerar etiquetas");
    }
  };

  const printLabels = () => {
    if (!labelData) return;

    const escapeLabelText = (value) => String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

    const labels = Array.isArray(labelData.labels) ? labelData.labels : [];
    if (!labels.length) {
      toast.error('Não existem etiquetas para imprimir');
      return;
    }

    // A writable about:blank window is required to inject the exact print CSS.
    // `noopener` cannot be used here because some browsers then return no handle.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Falha ao abrir janela de impressão');
      return;
    }

    const labelMarkup = labels.map((label) => {
      const numericPrice = Number(label.price || 0);
      const lot = escapeLabelText(String(label.lot_number || '').slice(0, 10));
      const expiry = escapeLabelText(label.expiry_date);
      return `
        <section class="label">
          <div class="product-name">${escapeLabelText(label.product_name)}</div>
          <div class="label-bottom">
            <div class="label-details">
              <div class="price">${numericPrice.toFixed(2).replace('.', ',')} €</div>
              ${lot ? `<div class="meta">Lote: ${lot}</div>` : ''}
              ${expiry ? `<div class="meta">Val.: ${expiry}</div>` : ''}
            </div>
            <img class="qr" src="${label.qr_image || ''}" alt="QR do lote" />
          </div>
        </section>`;
    }).join('');

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
      <html lang="pt"><head><meta charset="utf-8"><title>Etiquetas 35x25</title>
      <style>
        @page { size: 35mm 25mm; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; width: 35mm; background: #fff; }
        body { font-family: Arial, Helvetica, sans-serif; color: #000; }
        .label {
          width: 35mm; height: 25mm; padding: 1mm;
          overflow: hidden; break-after: page; page-break-after: always;
          background: #fff;
        }
        .label:last-child { break-after: auto; page-break-after: auto; }
        .product-name {
          height: 7mm; overflow: hidden; font-size: 7pt; line-height: 1.12;
          font-weight: 700; overflow-wrap: anywhere;
        }
        .label-bottom {
          height: 16mm; display: flex; align-items: flex-end;
          justify-content: space-between; gap: 0.8mm;
        }
        .label-details { min-width: 0; flex: 1; padding-bottom: 0.9mm; }
        .price { font-size: 11pt; line-height: 1; font-weight: 700; white-space: nowrap; margin-bottom: 1mm; }
        .meta { font-size: 4.5pt; line-height: 1.15; font-weight: 700; white-space: nowrap; overflow: hidden; }
        .qr {
          display: block; width: 13mm; height: 13mm; flex: 0 0 13mm;
          margin-right: 0.8mm; margin-bottom: 0.4mm;
          object-fit: contain; image-rendering: pixelated;
        }
        @media screen {
          body { background: #ddd; }
          .label { outline: 1px solid #999; }
        }
        @media print {
          html, body { width: 35mm !important; }
          .label { outline: none; }
        }
      </style></head><body>${labelMarkup}
      <script>
        window.addEventListener('load', function () {
          window.focus();
          window.print();
        }, { once: true });
      <\/script></body></html>`);
    printWindow.document.close();

    if (selectedBatch?.id) {
      batchesAPI.markLabelsPrinted(selectedBatch.id)
        .then(() => loadData())
        .catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Gestão de Stock</h1>
          <p className="text-slate-400">Lotes, validades e entrada de mercadoria</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="add-batch-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Entrada de Mercadoria
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => {
            setOnlyBelowMinStock(false);
            setOnlyNoStock(false);
            setOnlyExpired(false);
          }}
          className="text-left"
        >
          <Card className="card-dark p-4 hover:border-emerald-500/40 transition">
            <p className="text-sm text-slate-400">Produtos com lotes</p>
            <p className="text-2xl font-bold text-white">{stockSummary.totalProductsWithLots}</p>
          </Card>
        </button>

        <button
          type="button"
          onClick={() => {
            setOnlyBelowMinStock(true);
            setOnlyNoStock(false);
            setOnlyExpired(false);
          }}
          className="text-left"
        >
          <Card className="card-dark p-4 hover:border-amber-500/40 transition">
            <p className="text-sm text-slate-400">Abaixo do mínimo</p>
            <p className="text-2xl font-bold text-amber-400">{stockSummary.belowMin}</p>
          </Card>
        </button>

        <button
          type="button"
          onClick={() => {
            setOnlyBelowMinStock(false);
            setOnlyNoStock(true);
            setOnlyExpired(false);
          }}
          className="text-left"
        >
          <Card className="card-dark p-4 hover:border-slate-400/40 transition">
            <p className="text-sm text-slate-400">Sem stock</p>
            <p className="text-2xl font-bold text-slate-200">{stockSummary.noStock}</p>
          </Card>
        </button>

        <button
          type="button"
          onClick={() => {
            setOnlyBelowMinStock(false);
            setOnlyNoStock(false);
            setOnlyExpired(true);
          }}
          className="text-left"
        >
          <Card className="card-dark p-4 hover:border-rose-500/40 transition">
            <p className="text-sm text-slate-400">Com expirados</p>
            <p className="text-2xl font-bold text-rose-400">{stockSummary.expired}</p>
          </Card>
        </button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="batches" className="data-[state=active]:bg-emerald-500">
            <Package className="w-4 h-4 mr-2" />
            Todos os Lotes
          </TabsTrigger>
          <TabsTrigger value="expiring" className="data-[state=active]:bg-amber-500">
            <AlertTriangle className="w-4 h-4 mr-2" />
            A Expirar ({expiringBatches.length})
          </TabsTrigger>
          <TabsTrigger value="labels" className="data-[state=active]:bg-yellow-500">
            <ClipboardList className="w-4 h-4 mr-2" />
            Etiquetas ({pendingLabels.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="batches" className="space-y-4">
          {/* Search + Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Pesquisar produto ou lote..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
                data-testid="search-batches"
              />
            </div>

            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-[220px] bg-slate-800 border-slate-700">
                <SelectValue placeholder="Fornecedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os fornecedores</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant={onlyBelowMinStock ? "default" : "outline"}
              onClick={() => setOnlyBelowMinStock((v) => !v)}
              className={onlyBelowMinStock ? "bg-amber-500 hover:bg-amber-600 text-slate-950" : "border-slate-700"}
            >
              Abaixo do mínimo
            </Button>

            <Button
              type="button"
              variant={onlyNoStock ? "default" : "outline"}
              onClick={() => setOnlyNoStock((v) => !v)}
              className={onlyNoStock ? "bg-slate-500 hover:bg-slate-600 text-white" : "border-slate-700"}
            >
              Sem stock
            </Button>

            <Button
              type="button"
              variant={onlyExpired ? "default" : "outline"}
              onClick={() => setOnlyExpired((v) => !v)}
              className={onlyExpired ? "bg-rose-500 hover:bg-rose-600 text-white" : "border-slate-700"}
            >
              Com expirados
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={exportStockCSV}
              className="border-slate-700"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={exportStockPDF}
              className="border-slate-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              PDF
            </Button>

            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-1.5">
              <span className="px-1 text-xs text-slate-400">Inventário à data</span>
              <Input type="date" value={inventoryAsOf} onChange={(e) => setInventoryAsOf(e.target.value)} className="h-9 w-40 bg-slate-800 border-slate-700" />
              <Button type="button" size="sm" variant="outline" onClick={() => downloadInventory('at-csv')} className="border-emerald-600/50">AT CSV</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => downloadInventory('at-xml')} className="border-emerald-600/50">AT XML</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => downloadInventory('pdf')} className="border-slate-600">PDF Inventário</Button>
            </div>

            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSupplierFilter("all");
                setOnlyBelowMinStock(false);
                setOnlyNoStock(false);
                setOnlyExpired(false);
                setExpiryStatusFilter("all");
                setSearchQuery("");
              }}
              className="text-slate-300 hover:text-white"
            >
              Limpar filtros
            </Button>
          </div>

          {/* Batches Table */}
          <Card className="card-dark">
            <Table className="table-dark">
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button type="button" onClick={() => handleSort('product')} className="flex items-center gap-1 hover:text-white transition">
                      Produto {renderSortIcon('product')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" onClick={() => handleSort('lot_number')} className="flex items-center gap-1 hover:text-white transition">
                      Lote {renderSortIcon('lot_number')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" onClick={() => handleSort('expiry_date')} className="flex items-center gap-1 hover:text-white transition">
                      Validade {renderSortIcon('expiry_date')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => handleSort('quantity')} className="ml-auto flex items-center gap-1 hover:text-white transition">
                      Quantidade {renderSortIcon('quantity')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" onClick={() => handleSort('cost_price')} className="ml-auto flex items-center gap-1 hover:text-white transition">
                      Custo {renderSortIcon('cost_price')}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const grouped = groupedFilteredProducts;

                  if (Object.keys(grouped).length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>Nenhum lote encontrado</p>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  let groupedEntries = Object.entries(grouped);

                  if (sortField === 'product') {
                    groupedEntries = groupedEntries.sort(([, a], [, b]) =>
                      compareValues(a.product?.name || '', b.product?.name || '', sortDirection)
                    );
                  } else {
                    groupedEntries = groupedEntries.sort(([, a], [, b]) =>
                      compareValues(a.product?.name || '', b.product?.name || '', 'asc')
                    );
                  }

                  return groupedEntries.map(([pid, group]) => {
                    const groupDraftReturnQuantity = group.batches.reduce((sum, item) => sum + Number(item.draft_return_quantity || 0), 0);
                    const sortedGroupBatches = [...group.batches].sort((a, b) => {
                      switch (sortField) {
                        case 'lot_number':
                          return compareValues(a.lot_number || '', b.lot_number || '', sortDirection);
                        case 'expiry_date':
                          return compareValues(a.expiry_date || '', b.expiry_date || '', sortDirection);
                        case 'quantity':
                          return compareValues(Number(a.quantity || 0), Number(b.quantity || 0), sortDirection);
                        case 'cost_price':
                          return compareValues(Number(a.cost_price || 0), Number(b.cost_price || 0), sortDirection);
                        default:
                          return compareValues(a.expiry_date || '', b.expiry_date || '', 'asc');
                      }
                    });
                    const nearestExpiry = group.batches
                      .map(item => item.expiry_date)
                      .filter(Boolean)
                      .sort((a, b) => String(a).localeCompare(String(b)))[0] || '';

                    return (
                      <React.Fragment key={pid}>
                        <TableRow
                          className="bg-slate-800/60 border-t border-slate-700 cursor-pointer hover:bg-slate-700/70 transition-colors"
                          onClick={() => setExpandedProductId(current => current === pid ? null : pid)}
                        >
                          <TableCell className="font-bold text-white">
                            <button
                              type="button"
                              className="flex items-center gap-2 text-left hover:text-emerald-300 transition w-full"
                              title={expandedProductId === pid ? "Fechar lotes" : "Ver lotes"}
                            >
                              {expandedProductId === pid ? <ChevronDown className="w-4 h-4 text-emerald-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                              <Package className="w-4 h-4 text-emerald-400" />
                              <span>{group.product?.name || 'Desconhecido'}{groupDraftReturnQuantity > 0 && <span className="ml-2 text-xs font-semibold text-amber-300">({groupDraftReturnQuantity} un. retidas em rascunho)</span>}</span>
                            </button>
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">{group.batches.length} lote(s)</TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {nearestExpiry || '—'}
                          </TableCell>
                          <TableCell className="text-right font-bold text-emerald-400">{group.totalStock} un</TableCell>
                          <TableCell className="text-right text-slate-400 text-xs">Stock Total</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar artigo"
                              onClick={(event) => { event.stopPropagation(); openEditProductModal(group.product); }}
                            >
                              <PencilLine className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {expandedProductId === pid && sortedGroupBatches.map((batch) => {
                          const days = getDaysUntilExpiry(batch.expiry_date);
                          return (
                            <TableRow key={batch.id} className="bg-slate-900/40 hover:bg-slate-800/40">
                              <TableCell className="pl-10 text-slate-500 text-xs">Detalhe</TableCell>
                              <TableCell className="text-slate-400 text-sm">
                                <Badge variant="outline" className="border-slate-600 font-mono text-xs">
                                  {batch.lot_number || '—'}
                                </Badge>
                              </TableCell>
                              <TableCell className={Number(batch.draft_return_quantity || 0) > 0 ? 'opacity-60' : ''}>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                                  <span className={`text-sm ${getExpiryStyle(batch.expiry_status)}`}>
                                    {batch.expiry_date || '—'}
                                    {batch.expiry_status && (
                                      <span className="ml-2 text-xs opacity-80">
                                        ({getExpiryLabel(batch.expiry_status)})
                                      </span>
                                    )}
                                  </span>
                                  {days <= 90 && getExpiryBadge(days)}
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-slate-300 text-sm">{batch.quantity} un{Number(batch.draft_return_quantity || 0)>0&&<div className="text-xs text-amber-300">({batch.draft_return_quantity} retidas · stock não baixado)</div>}</TableCell>
                              <TableCell className="text-right text-slate-400 text-sm">
                                €{(batch.cost_price || 0).toFixed(2)}/un
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => openLabelModal(batch)} title="Imprimir etiqueta">
                                    <Printer className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
                                    title="Corrigir lote"
                                    onClick={() => openCorrectionModal(batch)}
                                  >
                                    <PencilLine className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" title="Histórico de correções" onClick={() => openCorrectionHistory(batch)}>
                                    <History className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="labels" className="space-y-4">
          <Card className="card-dark border-amber-500/20">
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Etiquetas por imprimir</h3>
                    <p className="text-sm text-slate-400">
                      Lotes novos que entraram em stock e ainda aguardam impressão de etiquetas.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700">
                    <p className="text-xs text-slate-400">Pendentes</p>
                    <p className="text-xl font-bold text-amber-300">{pendingLabels.length}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                    onClick={() => setShowPendingLabelsModal(true)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Ver pendentes
                  </Button>
                </div>
              </div>

              {pendingLabels.length > 0 ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {pendingLabels.map((item) => (
                    <div key={item.id} className="rounded-xl border border-slate-700 bg-slate-800/70 p-3">
                      <p className="text-sm font-medium text-white">{item.product_name}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Lote: {item.lot_number || '—'} | Qtd: {item.quantity || 0}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Validade: {item.expiry_date || '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {item.entry_source === 'invoice' ? 'Entrada por fatura' : 'Entrada manual'}
                      </p>

                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-slate-600"
                          onClick={() => openPendingLabelFromRow(item)}
                        >
                          <Tag className="w-4 h-4 mr-1" />
                          Abrir
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          onClick={() => markPendingLabelPrinted(item.id)}
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          Marcar impresso
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center">
                  <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-500 opacity-50" />
                  <p className="text-slate-400">Não há etiquetas pendentes neste momento.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expiring" className="space-y-4">
          {/* Filter */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-slate-300">Expirar em:</Label>
              <Select value={expiryFilter.toString()} onValueChange={(val) => setExpiryFilter(parseInt(val))}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                  <SelectItem value="180">180 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={exportExpiringCSV}
              className="border-slate-700"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={exportExpiringPDF}
              className="border-slate-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>

          {/* Expiring Batches */}
          <div className="grid gap-4">
            {chronologicalExpiringBatches.map((batch) => {
              const days = getDaysUntilExpiry(batch.expiry_date);
              return (
                <Card key={batch.id} className={`card-dark p-4 ${Number(batch.draft_return_quantity || 0)>0?'opacity-70 border-amber-500/30':''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        days <= 30 ? 'bg-rose-500/20' : days <= 60 ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                      }`}>
                        <AlertTriangle className={`w-6 h-6 ${
                          days <= 30 ? 'text-rose-400' : days <= 60 ? 'text-amber-400' : 'text-emerald-400'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{batch.product_name}</p>
                        <p className="text-sm text-slate-500">
                          Lote: {batch.lot_number} | Validade: {batch.expiry_date}
                        </p>
                        {Number(batch.draft_return_quantity || 0)>0&&<p className="text-xs text-amber-300 mt-1">{batch.draft_return_quantity} un. fisicamente retidas em rascunho ({(batch.draft_return_documents||[]).join(', ')}) · stock contabilístico mantém-se</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-white">{batch.quantity} un</p>
                        {getExpiryBadge(days)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLabelModal(batch)}
                        className="border-slate-700"
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Etiquetas
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
            {chronologicalExpiringBatches.length === 0 && (
              <Card className="card-dark p-8 text-center">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-500 opacity-50" />
                <p className="text-slate-500">Nenhum produto a expirar nos próximos {expiryFilter} dias</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Batch Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Entrada de Mercadoria</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Produto</Label>

              {!formData.product_id ? (
                <>
                  <Input
                    placeholder="Pesquisar produto..."
                    value={productModalSearch}
                    onChange={e => setProductModalSearch(e.target.value)}
                    className="bg-slate-800 border-slate-700 mb-2"
                  />

                  <div className="max-h-52 overflow-auto rounded-xl border border-slate-800 bg-slate-950/60 shadow-inner">
                    {filteredModalProducts.length > 0 ? (
                      filteredModalProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleProductSelect(product.id)}
                          className="w-full text-left px-4 py-3 border-b border-slate-800 last:border-b-0 hover:bg-slate-800 transition"
                        >
                          <div className="text-base text-white font-medium">{product.name}</div>
                          <div className="text-xs text-slate-400 mt-1">
                            Ref: {product.reference || '—'} | Código: {product.barcode || '—'}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Stock: {product.total_stock ?? 0} | Custo: €{Number(product.cost_price || 0).toFixed(2)} | PVP: €{Number(product.sell_price || 0).toFixed(2)}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        Nenhum produto encontrado
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white font-semibold">{selectedProduct?.name || 'Produto selecionado'}</p>
                      <p className="text-xs text-slate-300 mt-1">
                        Ref: {selectedProduct?.reference || '—'} | Código: {selectedProduct?.barcode || '—'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Stock atual: {selectedProduct?.total_stock ?? 0} | Custo sugerido: €{Number(selectedProduct?.cost_price || 0).toFixed(2)} | PVP atual: €{Number(selectedProduct?.sell_price || 0).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditProductModal(selectedProduct)}
                        className="text-xs text-emerald-300 hover:text-emerald-200 underline"
                      >
                        Editar artigo
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, product_id: '', cost_price: 0, sell_price: 0 }));
                          setProductModalSearch('');
                        }}
                        className="text-xs text-slate-300 hover:text-white underline"
                      >
                        Alterar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Número do Lote</Label>
                <Input
                  value={formData.lot_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  required
                  data-testid="batch-lot"
                />
              </div>
              <div>
                <Label className="text-slate-300">Data de Validade</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  required
                  data-testid="batch-expiry"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Quantidade</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  className="bg-slate-800 border-slate-700"
                  required
                  data-testid="batch-quantity"
                />
              </div>
              <div>
                <Label className="text-slate-300">Preço de Custo</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, cost_price: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="batch-cost"
                />
              </div>
              <div>
                <Label className="text-slate-300">PVP (IVA incl.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sell_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, sell_price: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="batch-sell-price"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Fornecedor (opcional)</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(val) => setFormData(prev => ({ ...prev, supplier_id: val }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="batch-supplier">
                  <SelectValue placeholder="Selecionar fornecedor" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="batch-notes"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-slate-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !formData.product_id}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-batch-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Registar Entrada
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Quick Edit Product Modal */}
      <Dialog open={showEditProductModal} onOpenChange={setShowEditProductModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Edição Rápida do Artigo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome</Label>
              <Input
                value={editProductForm.name}
                onChange={(e) => setEditProductForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Referência</Label>
                <Input
                  value={editProductForm.reference}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, reference: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-300">Código de Barras</Label>
                <Input
                  value={editProductForm.barcode}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, barcode: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Custo</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editProductForm.cost_price}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, cost_price: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-300">PVP</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editProductForm.sell_price}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, sell_price: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-300">Stock Mínimo</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={editProductForm.min_stock}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, min_stock: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditProductModal(false)}
              className="border-slate-700"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSaveProductQuickEdit}
              disabled={saving || !editProductForm.id}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader><DialogTitle className="text-white">Corrigir lote</DialogTitle></DialogHeader>
          {correctionBatch && <div className="space-y-4">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-slate-300">
              <p className="font-semibold text-white">{correctionBatch.product_name}</p>
              <p>A alteração ficará registada com operador, data, valores anteriores e justificação.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Quantidade correta</Label><Input type="number" min="0" step="1" inputMode="numeric" value={correctionForm.quantity} onChange={e => setCorrectionForm(p => ({...p, quantity: parseInt(e.target.value, 10) || 0}))} /></div>
              <div><Label>Número do lote</Label><Input value={correctionForm.lot_number} onChange={e => setCorrectionForm(p => ({...p, lot_number: e.target.value}))} /></div>
              <div><Label>Validade</Label><Input type="date" value={correctionForm.expiry_date || ''} onChange={e => setCorrectionForm(p => ({...p, expiry_date: e.target.value}))} /></div>
            </div>
            <div><Label>Justificação obrigatória</Label><textarea rows={3} value={correctionForm.reason} onChange={e => setCorrectionForm(p => ({...p, reason: e.target.value}))} placeholder="Ex.: quantidade introduzida incorretamente na receção" className="w-full mt-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-white" /></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={correcting} onClick={submitCorrection}>{correcting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Confirmar correção</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCorrectionHistoryModal} onOpenChange={setShowCorrectionHistoryModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl">
          <DialogHeader><DialogTitle className="text-white">Histórico de correções</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            {correctionHistory.length === 0 ? <p className="py-8 text-center text-slate-500">Este lote ainda não tem correções.</p> :
              <Table className="table-dark"><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Alteração</TableHead><TableHead>Justificação</TableHead><TableHead>Operador</TableHead></TableRow></TableHeader>
                <TableBody>{correctionHistory.map(item => <TableRow key={item.id}>
                  <TableCell className="whitespace-nowrap text-slate-400">{new Date(item.corrected_at).toLocaleString('pt-PT')}</TableCell>
                  <TableCell className="text-slate-300"><div>Qtd.: {item.old_values?.quantity} → {item.new_values?.quantity} ({Number(item.quantity_delta) >= 0 ? '+' : ''}{item.quantity_delta})</div><div>Lote: {item.old_values?.lot_number || '—'} → {item.new_values?.lot_number || '—'}</div><div>Val.: {item.old_values?.expiry_date || '—'} → {item.new_values?.expiry_date || '—'}</div></TableCell>
                  <TableCell className="text-white">{item.reason}</TableCell><TableCell className="text-slate-400">{item.corrected_by_email || item.corrected_by}</TableCell>
                </TableRow>)}</TableBody></Table>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPendingLabelsModal} onOpenChange={setShowPendingLabelsModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-amber-400" />
              Etiquetas pendentes de impressão
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
              <p className="text-sm text-slate-300">
                Lista dos lotes recém-entrados em stock que ainda aguardam etiquetagem.
              </p>
            </div>

            <div className="max-h-[60vh] overflow-auto rounded-xl border border-slate-800">
              <Table className="table-dark">
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead>Validade</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingLabels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Não há etiquetas pendentes neste momento.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingLabels.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-white font-medium">{item.product_name}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-slate-800 rounded text-xs text-emerald-400">
                            {item.lot_number || '—'}
                          </code>
                        </TableCell>
                        <TableCell className="text-slate-400">{item.expiry_date || '—'}</TableCell>
                        <TableCell className="text-right text-white">{item.quantity || 0}</TableCell>
                        <TableCell className="text-slate-400">
                          {item.entry_source === 'invoice' ? 'Fatura' : 'Manual'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              onClick={() => openPendingLabelFromRow(item)}
                            >
                              <Printer className="w-4 h-4 mr-1" />
                              Imprimir
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-600 text-slate-300 hover:bg-slate-700/40"
                              onClick={() => markPendingLabelPrinted(item.id)}
                            >
                              Marcar impresso
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPendingLabelsModal(false)}
              className="border-slate-700"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Label Modal */}
      <Dialog open={showLabelModal} onOpenChange={setShowLabelModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-400" />
              Gerar Etiquetas
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedBatch && (
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="font-medium text-white">{selectedBatch.product_name}</p>
                <p className="text-sm text-slate-400">
                  Lote: {selectedBatch.lot_number} | Val: {selectedBatch.expiry_date}
                </p>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Quantidade de Etiquetas</Label>
              <Input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                max="100"
                value={labelQuantity}
                onChange={(e) => setLabelQuantity(parseInt(e.target.value) || 1)}
                className="bg-slate-800 border-slate-700"
                data-testid="label-quantity"
              />
            </div>

            {/* Label Preview */}
            {labelData && (
              <div className="p-4 bg-white rounded-lg">
                <div className="label-preview mx-auto" style={{ width: '35mm', height: '25mm' }}>
                  <p style={{
                    fontSize: '7pt', fontWeight: 'bold', color: 'black',
                    lineHeight: 1.15, height: '7mm', overflow: 'hidden',
                  }}>
                    {labelData.labels[0]?.product_name}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '16mm' }}>
                    <p style={{ fontSize: '11.5pt', fontWeight: 'bold', color: 'black', whiteSpace: 'nowrap' }}>
                      {labelData.labels[0]?.price.toFixed(2).replace('.', ',')} €
                    </p>
                    <div style={{
                      width: '13.5mm', height: '13.5mm', background: '#fff', color: '#000',
                      border: '1.2mm solid #000', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '7pt', fontWeight: 'bold',
                    }}>
                      QR
                    </div>
                  </div>
                </div>
                <p className="text-center text-xs text-slate-500 mt-2">
                  Formato: 35x25mm
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLabelModal(false)}
              className="border-slate-700"
            >
              Fechar
            </Button>
            {!labelData ? (
              <Button
                onClick={generateLabels}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="generate-labels-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Gerar
              </Button>
            ) : (
              <>
                <Button
                  onClick={downloadLabelsPDF}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="download-labels-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF c/ QR
                </Button>
                <Button
                  onClick={printLabels}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="print-labels-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockPage;
